/**
 * Error toast utility for displaying API errors with click-to-view functionality.
 *
 * @module utils/errorToast
 */

import toast from 'react-hot-toast';
import { useErrorStore } from '@/stores/useErrorStore';

/**
 * Display an error toast with a "Click for details" hint.
 * Clicking the toast opens the error drawer for full details.
 *
 * @param message - The error message to display
 * @param options - Optional toast configuration
 *
 * @example
 * showErrorToast('Failed to create sandbox: Server error');
 */
export function showErrorToast(message: string, options?: { duration?: number }): void {
  toast(
    (t) => (
      <div
        className="cursor-pointer"
        onClick={() => {
          toast.dismiss(t.id);
          useErrorStore.getState().openDrawer();
        }}
      >
        <div className="font-medium">{message}</div>
        <div className="text-xs opacity-75 mt-0.5">Click for details</div>
      </div>
    ),
    {
      duration: options?.duration ?? 5000,
      style: {
        background: '#ef4444',
        color: '#fff',
      },
      icon: '⚠️',
    }
  );
}

/**
 * Display a standard error toast without click functionality.
 * Use this for simple errors that don't need the drawer.
 *
 * @param message - The error message to display
 */
export function showSimpleErrorToast(message: string): void {
  toast.error(message, { duration: 4000 });
}
